import { useMemo } from 'react';
import { useStores } from '../../Stores/hooks';
import { CREATOR_PICKER_LIMIT } from '../constants';
import { useCreators } from './useCreatorQueries';

export interface SelectOption {
  value: string;
  label: string;
}

export interface OptionsResult {
  options: SelectOption[];
  loading: boolean;
}

/**
 * Creator options for a picker, labelled `Name (PREFIX)`.
 *
 * One page of `CREATOR_PICKER_LIMIT` covers most merchants; the list endpoint caps
 * `limit` at 100, so this is the widest single call available.
 */
export function useCreatorOptions(): OptionsResult {
  const { data, isLoading } = useCreators(1, CREATOR_PICKER_LIMIT);

  const options = useMemo(
    () =>
      (data?.data ?? []).map((creator) => ({
        value: creator.id,
        label: `${creator.name} (${creator.codePrefix})`,
      })),
    [data]
  );

  return { options, loading: isLoading };
}

/**
 * Store options for a picker.
 *
 * Backed by the shared `useStores` query (`staleTime: Infinity`), so mounting this
 * on several screens reuses one cached fetch rather than refetching per screen.
 */
export function useStoreOptions(): OptionsResult {
  const { data: stores, isLoading } = useStores();

  const options = useMemo(
    () => (stores ?? []).map((store) => ({ value: store.id, label: store.name })),
    [stores]
  );

  return { options, loading: isLoading };
}

/** Store id → name, for rendering a terms row's `storeIds`. */
export function useStoreNamesById(): Record<string, string> {
  const { data: stores } = useStores();

  return useMemo(
    () => Object.fromEntries((stores ?? []).map((store) => [store.id, store.name])),
    [stores]
  );
}
